import React, { useState, useMemo } from 'react';
import { PersonnelConfig, Service, Competence } from '../../../../../../shared/types/config';

interface PersonnelModalProps {
  editingPersonnel: PersonnelConfig | null;
  personnelForm: Omit<PersonnelConfig, 'id'>;
  setPersonnelForm: React.Dispatch<React.SetStateAction<Omit<PersonnelConfig, 'id'>>>;
  services: Service[];
  competences: Competence[];
  onClose: () => void;
  onSave: () => Promise<void>;
}

const inputCls = 'w-full rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#3d4fdb]/60';
const labelCls = 'block text-xs font-medium text-slate-400 mb-1.5';

const PersonnelModal: React.FC<PersonnelModalProps> = ({
  editingPersonnel,
  personnelForm,
  setPersonnelForm,
  services,
  competences,
  onClose,
  onSave,
}) => {
  const [searchCompetence, setSearchCompetence] = useState('');

  const filteredAndGroupedCompetences = useMemo(() => {
    const sid = personnelForm.id_service;
    const filtered = competences.filter((competence) => {
      if (!sid || sid <= 0) return false;
      if (competence.id_service !== null && competence.id_service !== sid) return false;
      if (searchCompetence) {
        const q = searchCompetence.toLowerCase();
        if (!competence.nom.toLowerCase().includes(q) && !(competence.description?.toLowerCase().includes(q))) return false;
      }
      return true;
    });

    const competencesByService = filtered.reduce((acc, competence) => {
      const serviceId = competence.id_service || 0;
      const serviceName = competence.id_service
        ? services.find(s => s.id === competence.id_service)?.nom || `Service ${serviceId}`
        : 'Générales (tous services)';
      if (!acc[serviceName]) acc[serviceName] = [];
      acc[serviceName].push(competence);
      return acc;
    }, {} as Record<string, Competence[]>);

    Object.keys(competencesByService).forEach(serviceName => {
      competencesByService[serviceName].sort((a, b) => {
        const ordreA = a.ordre ?? 999999;
        const ordreB = b.ordre ?? 999999;
        if (ordreA !== ordreB) return ordreA - ordreB;
        return a.nom.localeCompare(b.nom);
      });
    });

    const serviceNames = Object.keys(competencesByService).sort((a, b) => {
      if (a === 'Générales (tous services)') return -1;
      if (b === 'Générales (tous services)') return 1;
      return a.localeCompare(b);
    });

    return { competencesByService, serviceNames, filtered };
  }, [competences, services, personnelForm.id_service, searchCompetence]);

  const handleSelectAll = () => {
    const filteredComps = filteredAndGroupedCompetences.filtered;
    const allSelected = filteredComps.every(c => personnelForm.id_competences.includes(c.id!));
    if (allSelected) {
      setPersonnelForm({ ...personnelForm, id_competences: personnelForm.id_competences.filter(id => !filteredComps.some(c => c.id === id)) });
    } else {
      const newIds = filteredComps.map(c => c.id!).filter(id => id && !personnelForm.id_competences.includes(id));
      setPersonnelForm({ ...personnelForm, id_competences: [...personnelForm.id_competences, ...newIds] });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div className="relative z-50 rounded-xl border border-[#243052]/90 bg-[#0a1128] shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1e2d4d]">
          <h2 className="text-base font-bold text-white">
            {editingPersonnel ? 'Modifier le personnel' : 'Ajouter un personnel'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors p-1.5 hover:bg-[#1e2d4d] rounded-lg">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-130px)]">
          <div className="space-y-4">
            <div>
              <label className={labelCls}>Service du grade *</label>
              <select
                value={personnelForm.id_service}
                onChange={(e) => {
                  const nextSid = parseInt(e.target.value) || 0;
                  const validCompetences = nextSid > 0
                    ? personnelForm.id_competences.filter((cid) => {
                        const c = competences.find((x) => x.id === cid);
                        return c && (c.id_service == null || c.id_service === nextSid);
                      })
                    : personnelForm.id_competences;
                  setPersonnelForm({ ...personnelForm, id_service: nextSid, id_competences: validCompetences });
                }}
                className={inputCls}
                required
              >
                <option value={0}>Sélectionner un service</option>
                {services.map((service) => (
                  <option key={service.id} value={service.id}>{service.nom}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                Seules les compétences de ce service (et les générales) seront proposées.
              </p>
            </div>
            <div>
              <label className={labelCls}>Grade *</label>
              <input
                type="text"
                value={personnelForm.grade}
                onChange={(e) => setPersonnelForm({ ...personnelForm, grade: e.target.value })}
                className={inputCls}
                required
              />
            </div>
            <div>
              <label className={labelCls}>Prix *</label>
              <input
                type="number"
                value={personnelForm.prix}
                onChange={(e) => setPersonnelForm({ ...personnelForm, prix: parseFloat(e.target.value) || 0 })}
                className={inputCls}
                min="0"
                step="0.01"
                required
              />
            </div>
            <div>
              <label className={labelCls}>Rang du grade dans ce service</label>
              <input
                type="number"
                value={personnelForm.ordre ?? 0}
                onChange={(e) => setPersonnelForm({ ...personnelForm, ordre: parseInt(e.target.value, 10) || 0 })}
                className={inputCls}
                min="0"
              />
              <p className="text-xs text-slate-500 mt-1">
                Plus petit = affiché en premier (hiérarchie pour ce service uniquement).
              </p>
            </div>
            <div>
              <label className={labelCls}>
                Compétences *
                {personnelForm.id_competences.length > 0 && (
                  <span className="ml-2 text-xs text-[#8ea0f5]">
                    ({personnelForm.id_competences.length} sélectionnée{personnelForm.id_competences.length > 1 ? 's' : ''})
                  </span>
                )}
              </label>

              {personnelForm.id_service > 0 && competences.length > 8 && (
                <div className="mb-3 relative">
                  <input
                    type="text"
                    placeholder="Rechercher une compétence..."
                    value={searchCompetence}
                    onChange={(e) => setSearchCompetence(e.target.value)}
                    className={`${inputCls} pl-9`}
                  />
                  <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              )}

              <div className="rounded-lg border border-[#2b3a61] bg-[#0d1b3a]/80 p-4 max-h-64 overflow-y-auto">
                {!personnelForm.id_service ? (
                  <p className="text-amber-300/90 text-sm">
                    Choisissez d'abord le service pour afficher les compétences rattachées.
                  </p>
                ) : competences.length === 0 ? (
                  <p className="text-slate-400 text-sm">Aucune compétence disponible</p>
                ) : filteredAndGroupedCompetences.filtered.length === 0 ? (
                  <p className="text-slate-400 text-sm text-center py-4">
                    Aucune compétence pour ce service. Créez des compétences dans l'onglet Compétences.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {filteredAndGroupedCompetences.serviceNames.map(serviceName => (
                      <div key={serviceName}>
                        <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2 pb-1 border-b border-[#1e2d4d]">
                          {serviceName}
                        </div>
                        <div className="space-y-1">
                          {filteredAndGroupedCompetences.competencesByService[serviceName].map(competence => (
                            <label
                              key={competence.id}
                              className="flex items-center gap-2 text-slate-300 hover:text-white cursor-pointer p-2 rounded hover:bg-[#1e2d4d]/50 transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={personnelForm.id_competences.includes(competence.id!)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    if (competence.id) setPersonnelForm({ ...personnelForm, id_competences: [...personnelForm.id_competences, competence.id] });
                                  } else {
                                    setPersonnelForm({ ...personnelForm, id_competences: personnelForm.id_competences.filter(id => id !== competence.id) });
                                  }
                                }}
                                className="w-4 h-4 rounded"
                              />
                              <div className="flex-1">
                                <span className="text-sm">{competence.nom}</span>
                                {competence.is_specialite && (
                                  <span className="ml-2 px-2 py-0.5 bg-purple-500/20 text-purple-300 border border-purple-500/40 rounded text-xs">
                                    Spécialité
                                  </span>
                                )}
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {personnelForm.id_service > 0 && filteredAndGroupedCompetences.filtered.length > 0 && !searchCompetence && (
                <div className="mt-2 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="text-xs text-[#8ea0f5] hover:text-[#aabbff] transition-colors"
                  >
                    {filteredAndGroupedCompetences.filtered.every((c) => personnelForm.id_competences.includes(c.id!))
                      ? 'Tout désélectionner'
                      : 'Tout sélectionner'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#1e2d4d]">
          <button
            onClick={onClose}
            className="rounded-lg border border-white/15 bg-black/30 px-4 py-2 text-sm font-semibold text-slate-200 hover:bg-black/45 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={onSave}
            className="rounded-lg border border-[#3d4fdb]/40 bg-[#3d4fdb]/20 px-4 py-2 text-sm font-semibold text-[#8ea0f5] hover:bg-[#3d4fdb]/30 transition-colors"
          >
            {editingPersonnel ? 'Modifier' : 'Ajouter'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PersonnelModal;
